import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { Client } from "@/api/schemas";
import { ClientPicker, type PickerCommandGroup } from "@/components/clients/ClientPicker";
import { Modal } from "@/components/common/Modal";
import { destinosDeRol, rutaDeCliente, type Rol } from "@/routes/routeConfig";
import styles from "./CommandPalette.module.css";

interface CommandPaletteProps {
  rol: Rol;
  clients: readonly Client[];
  cliente: Client | null;
  recientes: readonly string[];
  onSelectClient: (client: Client) => void;
  onOpenBrake: () => void;
  onClose: () => void;
}

/**
 * `Cmd/Ctrl+K`: clientes (solo agencia), «Ir a» las secciones del cliente activo y «Acciones».
 * Es la misma lista que el selector de cliente, centrada y SIN animación: se abre siempre con teclado.
 */
export function CommandPalette({ rol, clients, cliente, recientes, onSelectClient, onOpenBrake, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const esAgencia = rol === "agency";

  const commandGroups = useMemo<PickerCommandGroup[]>(() => {
    const go = (path: string) => () => {
      onClose();
      navigate(path);
    };
    const groups: PickerCommandGroup[] = [];
    if (cliente) {
      const destinos = destinosDeRol(rol).map((destino) => ({ id: `ir-${destino.id}`, label: destino.label, onSelect: go(rutaDeCliente(cliente.slug, destino.ruta)) }));
      if (esAgencia) destinos.push({ id: "ir-historial", label: "Historial", onSelect: go(rutaDeCliente(cliente.slug, "propuestas/historial")) });
      groups.push({ id: "ir-a", title: "Ir a", commands: destinos });
    }
    if (esAgencia) {
      const acciones = [{ id: "nuevo-cliente", label: "Nuevo cliente", onSelect: go("/clientes/nuevo") }];
      if (cliente) {
        acciones.push(
          { id: "invitar", label: "Invitar a la tienda", onSelect: go(rutaDeCliente(cliente.slug, "equipo")) },
          { id: "conectar", label: "Conectar…", onSelect: go(rutaDeCliente(cliente.slug, "conexiones")) },
        );
      }
      acciones.push({ id: "parar", label: "Parar cambios", onSelect: () => { onClose(); onOpenBrake(); } });
      groups.push({ id: "acciones", title: "Acciones", commands: acciones });
    }
    return groups;
  }, [rol, cliente, esAgencia, navigate, onClose, onOpenBrake]);

  return (
    <Modal label="Buscar cliente, sección o acción" onClose={onClose} width="min(560px, 92vw)" animated={false}>
      <div className={styles.palette}>
        <ClientPicker
          clients={esAgencia ? clients : []}
          actualSlug={cliente?.slug ?? null}
          recientes={recientes}
          onSelect={(selected) => {
            onClose();
            if (selected.slug !== cliente?.slug) onSelectClient(selected);
          }}
          commandGroups={commandGroups}
          label="Buscar"
          placeholder="Ir a…"
          noResultsText="Sin resultados. Prueba con otro nombre."
        />
      </div>
    </Modal>
  );
}
